/*
Google Sheets service for storing RSVP data

SETUP INSTRUCTIONS (Method 1 - Google Apps Script Web App):
1. Open your Google Sheet
2. Go to Extensions > Apps Script
3. Create a new script with the following code:

function doPost(e) {
  var sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
  var data = JSON.parse(e.postData.contents);

  // Helpers to keep values readable in the sheet
  function yesNo(value) {
    if (value === true || value === 'true') return 'Yes';
    if (value === false || value === 'false') return 'No';
    if (value === 'yes') return 'Yes';
    if (value === 'no') return 'No';
    return '';
  }

  function accommodationLabel(value) {
    if (value === 'travel_home') return 'Hazautazom, nem foglalok szállást';
    if (value === 'self_arrange') return 'Saját magam intézem a szállást';
    return '';
  }

  var headers = [
    'Időbélyeg',
    'Név',
    'Email',
    'Telefon',
    'Eljössz?',
    'Kísérővel érkezik?',
    'Kísérő neve',
    'Üzenet',
    'Ételérzékenységed van-e?',
    'Milyen érzékenységed van?',
    'Szállás'
  ];

  // Ensure headers exist (row 1)
  if (sheet.getLastRow() === 0) {
    sheet.appendRow(headers);
  } else if (sheet.getLastRow() >= 1) {
    var existingHeaders = sheet.getRange(1, 1, 1, headers.length).getValues()[0];
    // If the first row doesn't match expected headers, we still proceed,
    // but you may want to align your columns to the 'headers' array.
  }

  // Build row values in the same order as headers
  var rowValues = [
    new Date(),
    (data.name || '').toString().trim(),
    (data.email || '').toString().trim(),
    (data.phone || '').toString().trim(),
    yesNo(data.areYouComing),
    yesNo(data.withAttendant),
    (data.attendantName || '').toString().trim(),
    (data.message || '').toString(),
    yesNo(data.hasFoodSensitivity),
    (data.foodSensitivityDetails || '').toString(),
    accommodationLabel(data.accommodationPlan)
  ];

  // UPSERT by Name (column 2): if name exists, update that row; otherwise append.
  var nameToFind = rowValues[1];
  if (!nameToFind) {
    return ContentService.createTextOutput(JSON.stringify({ success: false, error: 'Name is required' }))
      .setMimeType(ContentService.MimeType.JSON);
  }

  var lastRow = sheet.getLastRow();
  var matchRow = 0;

  // Search in column 2 (Name), starting from row 2 (skip headers)
  if (lastRow >= 2) {
    var nameRange = sheet.getRange(2, 2, lastRow - 1, 1).getValues(); // [[name], [name], ...]
    for (var i = 0; i < nameRange.length; i++) {
      var existingName = (nameRange[i][0] || '').toString().trim();
      if (existingName === nameToFind) {
        matchRow = i + 2; // offset because range starts at row 2
        break;
      }
    }
  }

  if (matchRow > 0) {
    // Update existing row (write all columns A..K)
    sheet.getRange(matchRow, 1, 1, rowValues.length).setValues([rowValues]);
  } else {
    // Insert new row
    sheet.appendRow(rowValues);
  }

  return ContentService.createTextOutput(JSON.stringify({success: true}))
    .setMimeType(ContentService.MimeType.JSON);
}

4. Save the script
5. Deploy as Web App:
   - Click Deploy > New deployment
   - Type: Web app
   - Execute as: Me
   - Who has access: Anyone
   - Click Deploy
6. Copy the Web App URL and use it as GOOGLE_SHEETS_WEB_APP_URL

SETUP INSTRUCTIONS (Method 2 - Using Google Sheets API):
1. Enable Google Sheets API in Google Cloud Console
2. Create credentials (API Key or OAuth)
3. Share your Google Sheet with the service account email (if using service account)
4. Set up the configuration below
*/

const GOOGLE_SHEETS_WEB_APP_URL =
  process.env.REACT_APP_GOOGLE_SHEETS_WEB_APP_URL || "";

/**
 * Store RSVP data in Google Sheets using Google Apps Script Web App
 * @param {Object} formData - RSVP form data
 * @param {string} formData.name - Guest name
 * @param {string} formData.email - Guest email
 * @param {string} formData.phone - Guest phone
 * @param {string} formData.areYouComing - 'yes' or 'no'
 * @param {boolean} formData.withAttendant - Whether coming with attendant
 * @param {string} formData.attendantName - Attendant name (if applicable)
 * @param {string} formData.message - Message from guest
 * @param {string} formData.hasFoodSensitivity - 'yes' or 'no' (only if coming)
 * @param {string} formData.foodSensitivityDetails - Details if hasFoodSensitivity is 'yes'
 * @param {string} formData.accommodationPlan - 'travel_home' | 'self_arrange' (only if coming)
 * @returns {Promise} Promise that resolves when data is stored
 */
export const storeRSVPInGoogleSheets = async (formData) => {
  try {
    if (!GOOGLE_SHEETS_WEB_APP_URL) {
      throw new Error(
        "Google Sheets Web App URL is not configured. Please set REACT_APP_GOOGLE_SHEETS_WEB_APP_URL environment variable."
      );
    }

    // Prepare the data to send
    const data = {
      name: formData.name || "",
      email: formData.email || "",
      phone: formData.phone || "",
      areYouComing: formData.areYouComing || "",
      withAttendant: formData.withAttendant || false,
      attendantName: formData.attendantName || "",
      message: formData.message || "",
      hasFoodSensitivity: formData.hasFoodSensitivity || "",
      foodSensitivityDetails: formData.foodSensitivityDetails || "",
      accommodationPlan: formData.accommodationPlan || "",
    };

    // Send POST request to Google Apps Script Web App
    const response = await fetch(GOOGLE_SHEETS_WEB_APP_URL, {
      method: "POST",
      mode: "no-cors", // Google Apps Script doesn't support CORS, so we use no-cors
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    // Note: With no-cors mode, we can't read the response, but the data should be saved
    // If you need to verify success, you'll need to set up proper CORS in Google Apps Script
    // or use a backend server as a proxy

    return {
      success: true,
      message: "Data stored in Google Sheets successfully",
    };
  } catch (error) {
    console.error("Google Sheets error:", error);
    return {
      success: false,
      message: error.message || "Failed to store data in Google Sheets",
      error,
    };
  }
};

/**
 * Alternative method using Google Sheets API (requires proper CORS setup)
 * This method requires a backend server or proxy due to CORS restrictions
 */
export const storeRSVPInGoogleSheetsAPI = async (formData) => {
  // This would require a backend server to handle the Google Sheets API calls
  // due to CORS and authentication requirements
  // For client-side only solution, use the Web App method above
  throw new Error(
    "Google Sheets API method requires a backend server. Please use the Web App method instead."
  );
};
